import React, { useMemo } from 'react';
import LogoMustachBlack from './MustachLogo';
import { isDesktop, isIpad, isMobile } from '../../global/ScreenSizeHelper';

const APP_STORE_URL = 'https://apps.apple.com/app/idXXXX'; // ← your link
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=XXXX'; // ← your link

/* ───────────── Styles ───────────── */
const INK = '#181436';
const ORANGE = '#FF7A28';
const MUTED = '#5a5a5a';

const styles = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '100%',
    minHeight: 'calc(100dvh - 65px - 35px)', // header + footer
    padding: '32px 16px 40px',
    boxSizing: 'border-box',
    background:
      'radial-gradient(1200px 800px at 50% 40%, rgba(255,255,255,0.8) 0%, rgba(252,247,235,1) 50%), linear-gradient(180deg, #fcf7eb 0%, #f4ecd8 100%)',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '480px',
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontFamily: 'Roboto Black',
    // fontFamily: 'helvetica rounded bold',
    fontSize: isMobile ? '36px' : '56px',
    // fontWeight: '700',
    lineHeight: 1.1,
    letterSpacing: '0.5px',
    color: INK,
  },
  lede: {
    maxWidth: '350px',
    margin: '15px 0 0',
    // fontFamily: 'Playfair Display',
    fontSize: '16px',
    lineHeight: 1.3,
    color: MUTED,
  },
  stores: (wide, single) => ({
    display: 'grid',
    gridTemplateColumns: wide && !single ? '1fr 1fr' : '1fr',
    gap: '10px',
    width: '100%',
    maxWidth: single ? '280px' : '100%',
    marginTop: '28px',
  }),
  store: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    minHeight: '58px',
    padding: '10px 18px',
    boxSizing: 'border-box',
    borderRadius: '14px',
    background: INK,
    color: '#fff',
    textAlign: 'left',
    textDecoration: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  storeIcon: {
    width: '26px',
    height: '26px',
    flexShrink: 0,
    fill: 'currentColor',
  },
  storeSmall: {
    display: 'block',
    fontFamily: 'Inter Regular',
    fontSize: '11px',
    lineHeight: 1.1,
    opacity: 0.75,
  },
  storeBig: {
    fontFamily: 'Inter Regular',
    fontSize: '17px',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  alt: {
    margin: '12px 0 0',
    fontFamily: 'Inter Regular',
    fontSize: '14px',
    color: MUTED,
  },
  altLink: {
    color: ORANGE,
    fontWeight: 600,
    textDecoration: 'none',
  },
  web: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    width: '100%',
    marginTop: '28px',
    padding: '14px 16px',
    boxSizing: 'border-box',
    border: '1px dashed rgba(24,20,54,0.15)',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.55)',
    fontFamily: 'Inter Regular',
    fontSize: '14px',
    lineHeight: 1.5,
    color: MUTED,
    textAlign: 'left',
  },
  webIcon: {
    width: '20px',
    height: '20px',
    marginTop: '1px',
    flexShrink: 0,
    fill: 'none',
    stroke: ORANGE,
    strokeWidth: 1.8,
  },
  webBold: {
    color: INK,
    fontWeight: 600,
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: '24px',
  },
};

/* ───────────── Helpers ───────────── */
// Which store to show first. ScreenSizeHelper knows mobile vs desktop, not iOS vs Android.
const getPlatform = () => {
  if (isDesktop) return 'other';
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (isIpad || /iPhone|iPad|iPod/i.test(ua)) return 'ios';
  return 'other';
};

const AppleIcon = () => (
  <svg viewBox='0 0 24 24' style={styles.storeIcon} aria-hidden='true'>
    <path d='M16.4 12.6c0-2.4 2-3.6 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.5-1-2.5-3.9zM14 5.5c.7-.8 1.1-1.9 1-3-1 0-2.1.7-2.8 1.5-.6.7-1.2 1.8-1 2.9 1.1.1 2.1-.6 2.8-1.4z' />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox='0 0 24 24' style={styles.storeIcon} aria-hidden='true'>
    <path d='M4 2.8v18.4c0 .4.2.7.5.8l10-10-10-10c-.3.1-.5.4-.5.8zm12 7.8L5.9 2.1l12.3 7.1-2.2 1.4zm0 2.8 2.2 1.4-12.3 7.1L16 13.4zM19.3 10l2.3 1.3c.6.4.6 1.1 0 1.5L19.3 14 16.9 12l2.4-2z' />
  </svg>
);

/* ───────────── Component ───────────── */
const ComingSoonContent = ({ appStoreUrl = APP_STORE_URL, playStoreUrl = PLAY_STORE_URL }) => {
  const platform = useMemo(getPlatform, []);
  const wide = !isMobile; // buttons side by side on iPad + desktop, stacked on phones

  const ios = (
    <a key='ios' href={appStoreUrl} target='_blank' rel='noopener noreferrer' style={styles.store}>
      <AppleIcon />
      <span>
        <small style={styles.storeSmall}>Download on the</small>
        <strong style={styles.storeBig}>App Store</strong>
      </span>
    </a>
  );

  const android = (
    <a
      key='android'
      href={playStoreUrl}
      target='_blank'
      rel='noopener noreferrer'
      style={styles.store}
    >
      <PlayIcon />
      <span>
        <small style={styles.storeSmall}>Get it on</small>
        <strong style={styles.storeBig}>Google Play</strong>
      </span>
    </a>
  );

  // On a phone, show only that phone's store as the main button
  const stores = platform === 'ios' ? [ios] : platform === 'android' ? [android] : [ios, android];

  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        <h1 style={styles.title}>Get SingleCombo on your phone</h1>
        <p style={styles.lede}>Order from local restaurants you love, delivered to your door.</p>

        <div style={styles.stores(wide, stores.length === 1)}>{stores}</div>

        {platform !== 'other' && (
          <p style={styles.alt}>
            On another device?{' '}
            <a
              href={platform === 'ios' ? playStoreUrl : appStoreUrl}
              target='_blank'
              rel='noopener noreferrer'
              style={styles.altLink}
            >
              {platform === 'ios' ? 'Get it on Google Play' : 'Get it on the App Store'}
            </a>
          </p>
        )}

        <div style={styles.web}>
          <svg viewBox='0 0 24 24' style={styles.webIcon} aria-hidden='true'>
            <rect x='3' y='4' width='18' height='13' rx='2' />
            <path d='M8 21h8M12 17v4' />
          </svg>
          <span>
            <b style={styles.webBold}>Web and desktop ordering is coming soon.</b> For now, order on
            our iPhone or Android app.
          </span>
        </div>

        {/* <div style={styles.logo}>
          <LogoMustachBlack LipsSvg width={isMobile ? 300 : 800} height={isMobile ? 240 : 420} />
        </div> */}
      </div>
    </div>
  );
};

export default ComingSoonContent;
